import {
  buildGeolocationGetRequest,
  useFetchApiAndTransform,
  UseFetchDataResponse
} from "./useFetchApi";
import {
  optionalDate,
  OptionalDate
} from "@silversea-ssc/dotcom-api-types/OptionalDate";
import { PromosApiResponse } from "@silversea-ssc/dotcom-api-types/Promos";
import { useQuerySearch } from "src/utils/useQueryParams";
import {
  byConfigurationCode,
  expirationDateOrInfinite,
  isPromoActiveTodayWithOverride
} from "src/utils/promos";
import { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";

type RemotePromoItem = Omit<PromosApiResponse[number], "toDate"> & {
  toDate: OptionalDate; // this is because toDate will be removed from API response
  isActive: boolean;
};
export type RemotePromos = RemotePromoItem[];

export const useRemotePromos = () => {
  const querySearch = useQuerySearch();
  const request = buildGeolocationGetRequest<unknown>(
    `promos`,
    {},
    querySearch
  );

  const response = useFetchApiAndTransform(
    request,
    promosApiResponseToActiveRemotePromos(querySearch)
  );
  return {
    requestUrl: request,
    ...response
  };
};

const promosApiResponseToActiveRemotePromos =
  (querySearch: string | undefined) =>
  (items: PromosApiResponse): RemotePromos => {
    const allTokensPrefixed = prefixAllTokens(items);
    return items
      .map(withToDateValue(querySearch, allTokensPrefixed))
      .filter((p) => p.isActive);
  };

type RemotePromoResponse = UseFetchDataResponse<RemotePromoItem | undefined>;
export const useRemotePromo = (
  promoConfigurationCode: string | undefined
): RemotePromoResponse => {
  const allPromos = useRemotePromos();
  const promo = allPromos.data?.find(
    byConfigurationCode(promoConfigurationCode)
  );
  return {
    ...allPromos,
    data: promo
  };
};

const withToDateValue =
  (querySearch: string | undefined, allTokensPrefixed: TokenValues) =>
  <T extends { tokenValues: TokenValues }>(
    item: T
  ): T & { toDate: OptionalDate; isActive: boolean } => {
    const expirationDate = optionalDate(
      expirationDateOrInfinite(item.tokenValues)
    );
    const isActive = isPromoActiveTodayWithOverride(
      expirationDate,
      querySearch
    );
    return {
      ...item,
      isActive,
      toDate: expirationDate,
      tokenValues: { ...item.tokenValues, ...allTokensPrefixed }
    };
  };

export const prefixAllTokens = <
  T extends { configurationCode: string; tokenValues: TokenValues }
>(
  promos: readonly T[]
): TokenValues =>
  promos.reduce((acc, p) => {
    return {
      ...acc,
      ...Object.entries(p.tokenValues).reduce((acc, [key, value]) => {
        acc[`${p.configurationCode}-${key}`] = value;
        return acc;
      }, {})
    };
  }, {});
